import { apiClient, APIError } from './client';
import { useAuthStore } from '../store/authStore';
import { signRequestWithKey } from '../crypto/signing';
import type { ApiResponse, SignedRequest } from '@shared/types';
import { markCircleServerAccessRemoved } from '../utils/removedCircleAccess';

export function resolveDeviceForIdentity(identityId: string, scopeLabel: string) {
  const state = useAuthStore.getState();
  const trimmedIdentityId = (identityId || '').trim();
  if (!trimmedIdentityId) {
    throw new Error(`Local ${scopeLabel} identity is required`);
  }
  const device = Object.values(state.devices).find((entry) => entry.identityId === trimmedIdentityId);
  if (!device || device.identityId !== trimmedIdentityId) {
    throw new Error(`No device for ${scopeLabel} identity: ${trimmedIdentityId}`);
  }
  return device;
}

export async function buildSignedForIdentity<T>(
  type: string,
  payload: T,
  identityId: string,
  scopeLabel: string
): Promise<SignedRequest<T>> {
  const device = resolveDeviceForIdentity(identityId, scopeLabel);
  return signRequestWithKey(type, payload, device.deviceId, device.signingKey);
}

export function resolveDefaultIdentityServerUrl(identityId: string, scopeLabel: string): string | undefined {
  const state = useAuthStore.getState();
  const device = resolveDeviceForIdentity(identityId, scopeLabel);
  return state.identities[device.identityId]?.serverUrl;
}

export async function signedIdentityRequest<TPayload, TResult>(
  params: {
    path: string;
    type: string;
    payload: TPayload;
    localIdentityId: string;
    serverUrl?: string;
    scopeLabel: string;
  }
): Promise<ApiResponse<TResult>> {
  const signed = await buildSignedForIdentity(
    params.type,
    params.payload,
    params.localIdentityId,
    params.scopeLabel
  );
  try {
    return await apiClient.signedRequest(
      params.path,
      signed,
      params.serverUrl || resolveDefaultIdentityServerUrl(params.localIdentityId, params.scopeLabel)
    );
  } catch (error) {
    if (
      error instanceof APIError
      && error.responseJson
      && typeof error.responseJson === 'object'
      && (error.responseJson as { error?: { code?: string; message?: string } }).error?.code === 'UNAUTHORIZED'
      && (error.responseJson as { error?: { message?: string } }).error?.message === 'Device not found'
      && markCircleServerAccessRemoved(params.localIdentityId)
    ) {
      window.dispatchEvent(new CustomEvent('circlus:server-access-removed', {
        detail: { identityId: params.localIdentityId }
      }));
    }
    throw error;
  }
}
